/**
 * Login repair + audit — the production answer to "can all 40+ people
 * actually get into /log?".
 *
 * "Repair & audit everyone" normalizes every participant's email + phone,
 * merges duplicate-email registrations, then tries a battery of realistic
 * formats for EVERY participant through the exact same lookup the public
 * /log page uses. Anyone who fails is listed with the reason.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { Wrench, ArrowsClockwise, MagnifyingGlass, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";
import IdentifierEditor from "@/pages/admin/IdentifierEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, tone = "white" }) => (
  <div className="border border-white/15 bg-black/40 p-2.5">
    <div className={`text-[9px] uppercase tracking-[0.25em] font-bold ${tone === "good" ? "text-emerald-300" : tone === "bad" ? "text-[#FF1B91]" : "text-white/50"}`}>{label}</div>
    <div className="text-white font-black text-2xl mt-1">{value}</div>
  </div>
);

export default function LoginAuditPanel({ token }) {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [probeVal, setProbeVal] = useState("");
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [merging, setMerging] = useState("");
  const [editing, setEditing] = useState("");

  const mergeGroup = async (g) => {
    const keep = g.records[0];
    setMerging(keep.id);
    try {
      const r = await fetch(`${BACKEND}/api/admin/participants/merge`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ keep_id: keep.id, merge_ids: g.records.slice(1).map((x) => x.id) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      await runRepair();
    } catch (e) {
      setError(e?.message || "Merge failed");
    } finally {
      setMerging("");
    }
  };

  const loadDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/login-repair/diagnostics?limit=30`, { headers });
      setDiag(await r.json());
    } catch (e) {
      setDiag({ attempts: [], error: e?.message });
    } finally {
      setDiagLoading(false);
    }
  };

  const headers = { "X-Admin-Token": token };

  const loadLast = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/admin/login-repair/report`, { headers });
      const d = await r.json();
      if (r.ok && d.exists) setReport(d);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadLast(); }, [loadLast]);

  const runRepair = async () => {
    setRunning(true); setError("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/login-repair/run`, { method: "POST", headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setReport(d);
    } catch (e) {
      setError(e?.message || "Repair failed");
    } finally {
      setRunning(false);
    }
  };

  const runProbe = async () => {
    if (!probeVal.trim()) return;
    setProbing(true); setProbe(null);
    try {
      const r = await fetch(`${BACKEND}/api/admin/login-repair/probe?identifier=${encodeURIComponent(probeVal.trim())}`, { headers });
      setProbe(await r.json());
    } catch (e) {
      setProbe({ error: e?.message || "network" });
    } finally {
      setProbing(false);
    }
  };

  const a = report?.audit;
  const failing = a?.failing_detail || [];
  const allRows = a?.participants || [];

  return (
    <CollapsibleSection
      id="login-audit"
      testId="login-audit-panel"
      title="Login repair &amp; audit"
      icon={<Wrench size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Normalize every participant's email + phone, merge duplicate signups, then verify all of them can be found at /log."
      onRefresh={loadLast}
      defaultOpen
    >
      <div className="space-y-5">
        <div className="flex flex-wrap gap-3 items-center">
          <button
            type="button"
            data-testid="run-login-repair-btn"
            onClick={runRepair}
            disabled={running}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
          >
            <ArrowsClockwise size={12} weight="bold" className={running ? "animate-spin" : ""} />
            {running ? "Repairing & auditing…" : "Repair & audit everyone"}
          </button>
          {report?.ran_at && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              Last run {new Date(report.ran_at).toLocaleString()}
            </span>
          )}
          {error && <span className="text-[#FF1B91] text-xs" data-testid="login-audit-error">{error}</span>}
        </div>

        {a && (
          <div data-testid="login-audit-summary" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <Stat label="Total registered" value={a.total_participants} />
              <Stat label="Approved / active" value={a.approved_active ?? "—"} tone="good" />
              <Stat label="Email login passing" value={a.email_login_passing ?? "—"} tone="good" />
              <Stat label="Phone login passing" value={a.phone_login_passing ?? "—"} tone="good" />
              <Stat label="Fully able to log" value={a.participants_fully_passing ?? "—"} tone={a.participants_failing ? "bad" : "good"} />
              <Stat label="Failures (can't log in)" value={a.participants_failing} tone={a.participants_failing ? "bad" : "good"} />
            </div>

            {(a.partial_detail || []).length > 0 && (
              <div className="text-[11px] text-yellow-200/80 border border-yellow-500/25 bg-yellow-500/[0.04] px-3 py-2">
                {a.partial_detail.length} participant{a.partial_detail.length === 1 ? "" : "s"} can log in but not by every identifier (shared email or shared phone) — details below.
              </div>
            )}

            {report.owner_identity && (
              <div data-testid="owner-identity-result" className="border border-[#FF1B91]/40 bg-[#FF1B91]/[0.05] px-3 py-2 text-[11px] text-white/75">
                <div className="text-[9px] uppercase tracking-[0.25em] text-[#FF1B91] font-bold mb-1">Owner account ({report.owner_identity.email} / {report.owner_identity.phone})</div>
                <div>Action: <b className="text-white">{report.owner_identity.action}</b>
                  {report.owner_identity.record_id ? ` · record ${report.owner_identity.record_id}` : ""}
                  {report.owner_identity.minutes_preserved !== undefined ? ` · ${report.owner_identity.minutes_preserved} minutes preserved` : ""}
                  {(report.owner_identity.removed_ids || []).length ? ` · merged ${report.owner_identity.removed_ids.length} duplicate row(s)` : ""}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-white/60 border border-white/10 bg-black/20 px-3 py-2">
              <span>Rows repaired <b className="text-white">{report.repair?.rows_updated ?? 0}</b></span>
              <span>Emails normalized <b className="text-white">{report.repair?.emails_normalized ?? 0}</b></span>
              <span>Phones normalized <b className="text-white">{report.repair?.phones_normalized ?? 0}</b></span>
              <span>Duplicate signups merged <b className="text-white">{report.dedupe?.duplicate_email_groups_merged ?? 0}</b></span>
              <span>Build <span className="font-mono text-emerald-300">{a.repair_version}</span></span>
            </div>

            {(report.dedupe?.merged || []).length > 0 && (
              <div className="border border-emerald-500/30 bg-emerald-500/[0.04] px-3 py-2 text-[11px] text-white/70">
                <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300 font-bold mb-1">Duplicate registrations merged (shown once publicly, all logins still work)</div>
                {report.dedupe.merged.map((m) => (
                  <div key={m.kept_id} className="mb-1">
                    {m.name} · kept 1 of {m.removed_ids.length + 1} records · {m.minutes_after_merge} min
                    <div className="text-white/45">
                      Works at /log with: {[...(m.email_logins || []), ...(m.phone_logins || [])].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(report.dedupe?.possible_duplicates || []).length > 0 && (
              <div data-testid="possible-duplicates" className="border border-yellow-500/40 bg-yellow-500/[0.05]">
                <div className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.25em] font-bold text-yellow-300">
                  Possible duplicates — same name, no shared phone ({report.dedupe.possible_duplicates.length})
                </div>
                <div className="p-3 space-y-3">
                  {report.dedupe.possible_duplicates.map((g) => (
                    <div key={g.name} className="text-[11px] text-white/70">
                      <div className="text-white font-bold mb-1">{g.name}</div>
                      {g.records.map((r) => (
                        <div key={r.id} className="text-white/55">
                          {r.email || "no email"} · {r.phone || "no phone"} · {r.minutes} min · signed up {String(r.created_at).slice(0, 10)}
                        </div>
                      ))}
                      <div className="text-white/40 my-1">{g.note}</div>
                      <button
                        type="button"
                        data-testid={`merge-btn-${g.records[0].id}`}
                        onClick={() => mergeGroup(g)}
                        disabled={merging === g.records[0].id}
                        className="text-[9px] uppercase tracking-[0.2em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/30 disabled:opacity-40 px-3 py-1.5"
                      >
                        {merging === g.records[0].id ? "Merging…" : "Same person — merge into one"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(report.dedupe?.shared_phone_groups || []).length > 0 && (
              <div className="border border-yellow-500/30 bg-yellow-500/[0.04] px-3 py-2 text-[11px] text-white/70">
                <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-300 font-bold mb-1">Shared phone numbers (different people — they must use email)</div>
                {report.dedupe.shared_phone_groups.map((g) => (
                  <div key={g.phone_digits}>{g.phone_digits}: {g.people.map((p) => `${p.name} (${p.email})`).join(" · ")}</div>
                ))}
              </div>
            )}

            {failing.length === 0 ? (
              <div data-testid="login-audit-all-clear" className="flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/[0.06] px-3 py-3 text-sm text-emerald-200">
                <CheckCircle size={18} weight="bold" />
                All {a.total_participants} participants can log in with their registered email or phone.
              </div>
            ) : (              <div data-testid="login-audit-failing" className="border border-[#FF1B91]/50 bg-[#FF1B91]/[0.05]">
                <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF1B91]">
                  <WarningCircle size={14} weight="bold" /> {failing.length} participant{failing.length === 1 ? "" : "s"} cannot log in
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-white/40 uppercase tracking-[0.2em] text-[9px] border-b border-white/10">
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">Email</th>
                        <th className="p-2 text-left">Phone</th>
                        <th className="p-2 text-left">Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failing.map((f) => (
                        <tr key={f.id} className="border-b border-white/5">
                          <td className="p-2 text-white">{f.name}</td>
                          <td className="p-2 text-white/60">{f.email || "—"}</td>
                          <td className="p-2 text-white/60">{f.phone || "—"}</td>
                          <td className="p-2 text-[#FF1B91]">{f.why_failing}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(a.partial_detail || []).length > 0 && (
              <div data-testid="login-audit-partial" className="border border-yellow-500/40 bg-yellow-500/[0.05]">
                <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-yellow-300">
                  <WarningCircle size={14} weight="bold" /> {a.partial_detail.length} participant{a.partial_detail.length === 1 ? "" : "s"} can log in but some formats still fail
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-white/40 uppercase tracking-[0.2em] text-[9px] border-b border-white/10">
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">Email / Phone</th>
                        <th className="p-2 text-left">Formats that fail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.partial_detail.map((p) => (
                        <tr key={p.id} className="border-b border-white/5">
                          <td className="p-2 text-white">{p.name}</td>
                          <td className="p-2 text-white/60">{p.email || "—"} · {p.phone || "—"}</td>
                          <td className="p-2 text-yellow-200">
                            {(p.failing_formats || []).map((f, i) => (
                              <div key={i}><span className="font-mono">{JSON.stringify(f.tried)}</span> → {f.reason}{f.match_count > 1 ? ` (${f.match_count} matches)` : ""}</div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(report.dedupe?.merge_ready || []).length > 0 && (
              <div data-testid="merge-ready" className="border border-[#FF1B91]/45 bg-[#FF1B91]/[0.05]">
                <div className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF1B91]">
                  {report.dedupe.merge_ready.length} duplicate signup{report.dedupe.merge_ready.length === 1 ? "" : "s"} ready to merge
                </div>
                <div className="p-3 space-y-2 text-[11px] text-white/70">
                  {report.dedupe.merge_ready.map((g, i) => (
                    <div key={i}>
                      <b className="text-white">{g.name}</b> <span className="text-white/40">({g.reason})</span>
                      {g.records.map((r) => (
                        <div key={r.id} className="text-white/50">{r.email || "no email"} · {r.phone || "no phone"} · {r.minutes} min</div>
                      ))}
                    </div>
                  ))}
                  <div className="text-white/45 pt-1">
                    Nothing has been changed. Press "Repair &amp; audit everyone" above to merge these into one
                    record each — every email and phone keeps working, minutes are combined, and the removed
                    rows are archived.
                  </div>
                </div>
              </div>
            )}

            {(report.dedupe?.email_conflicts || []).length > 0 && (
              <div data-testid="email-conflicts" className="border border-yellow-500/40 bg-yellow-500/[0.05] px-3 py-2 text-[11px] text-white/70">
                <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-300 font-bold mb-1">Same email, different names — nothing merged, needs your call</div>
                {report.dedupe.email_conflicts.map((c) => (
                  <div key={c.email} className="mb-1">
                    <span className="text-white">{c.email}</span>: {c.people.map((p) => `${p.name} (${p.minutes} min${p.phone ? ", " + p.phone : ""})`).join(" · ")}
                    <div className="text-white/45">{c.note}</div>
                  </div>
                ))}
              </div>
            )}

            {(report.owner_identity?.other_people_with_this_phone || []).length > 0 && (
              <div data-testid="owner-phone-conflicts" className="border border-yellow-500/40 bg-yellow-500/[0.05] px-3 py-2 text-[11px] text-white/70">
                <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-300 font-bold mb-1">Your phone is also on someone else's registration (kept intact)</div>
                {report.owner_identity.other_people_with_this_phone.map((p) => (
                  <div key={p.id}>{p.name} · {p.email || "no email"} · {p.minutes} min — your phone still opens YOUR record; they should use their email</div>
                ))}
              </div>
            )}

            {(report.possible_missing_registrations || []).length > 0 && (
              <div data-testid="possible-missing" className="border border-white/20 bg-white/[0.03] px-3 py-2 text-[11px] text-white/70">
                <div className="text-[9px] uppercase tracking-[0.25em] text-white/60 font-bold mb-1">
                  In the system but NOT registered as participants ({report.possible_missing_registrations.length}) — check here if your count looks low
                </div>
                {report.possible_missing_registrations.slice(0, 25).map((m, i) => (
                  <div key={i}>{m.name} · {m.email || "—"} · {m.phone || "—"} <span className="text-white/40">({m.source})</span></div>
                ))}
              </div>
            )}

            {/* Full participant management list — always visible. */}
            <div data-testid="participant-management" className="border border-white/10">
              <div className="px-3 py-2 bg-white/[0.03] border-b border-white/10 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">All registered participants</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{allRows.length} total</div>
              </div>
              <div className="overflow-x-auto max-h-[520px]">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-[#0F0F0F]">
                    <tr className="text-white/40 uppercase tracking-[0.15em] text-[9px] border-b border-white/10">
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Email</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">Normalized email</th>
                      <th className="p-2 text-left">Normalized phone</th>
                      <th className="p-2 text-left">Signed up</th>
                      <th className="p-2 text-left">Login status</th>
                      <th className="p-2 text-right">Minutes</th>
                      <th className="p-2 text-center">Email login</th>
                      <th className="p-2 text-center">Phone login</th>
                      <th className="p-2 text-right">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((p) => (
                      <Fragment key={p.id}>
                        <tr data-testid={`participant-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                          <td className="p-2 text-white whitespace-nowrap">{p.name}</td>
                          <td className="p-2 text-white/60">{p.email || "—"}</td>
                          <td className="p-2 text-white/60 whitespace-nowrap">{p.phone || "—"}</td>
                          <td className="p-2 text-white/40 font-mono">{p.email_normalized || "—"}</td>
                          <td className="p-2 text-white/40 font-mono whitespace-nowrap">{p.phone_digits || "—"}</td>
                          <td className="p-2 text-white/40 whitespace-nowrap">{p.created_at ? String(p.created_at).slice(0, 10) : "—"}</td>
                          <td className={`p-2 text-[9px] uppercase tracking-[0.2em] font-bold ${p.log_access === "active" ? "text-emerald-300" : "text-yellow-300"}`}>{p.log_access}</td>
                          <td className="p-2 text-right text-white">{p.minutes_logged}</td>
                          <td className={`p-2 text-center font-bold ${p.email_login_ok ? "text-emerald-300" : p.has_email ? "text-[#FF1B91]" : "text-white/25"}`}>
                            {p.email_login_ok ? "PASS" : p.has_email ? "FAIL" : "n/a"}
                          </td>
                          <td className={`p-2 text-center font-bold ${p.phone_login_ok ? "text-emerald-300" : p.has_phone ? "text-[#FF1B91]" : "text-white/25"}`}>
                            {p.phone_login_ok ? "PASS" : p.has_phone ? "FAIL" : "n/a"}
                          </td>
                          <td className="p-2 text-right">
                            <button
                              type="button"
                              data-testid={`edit-logins-${p.id}`}
                              onClick={() => setEditing(editing === p.id ? "" : p.id)}
                              className="text-[9px] uppercase tracking-[0.2em] font-bold border border-white/25 hover:border-[#FF1B91] text-white px-2.5 py-1"
                            >
                              {editing === p.id ? "Close" : "Edit"}
                            </button>
                          </td>
                        </tr>
                        {editing === p.id && (
                          <tr>
                            <td colSpan={11} className="p-3 bg-black/30">
                              <IdentifierEditor
                                participant={p}
                                token={token}
                                onSaved={runRepair}
                                onClose={() => setEditing("")}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Live failures from the public /log page — exact cause, no curl. */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/50 font-bold">Recent failed logins on the live site</div>
            <button
              type="button"
              data-testid="load-diagnostics-btn"
              onClick={loadDiagnostics}
              className="text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-[#FF1B91]"
            >
              {diagLoading ? "Loading…" : "Load / refresh"}
            </button>
          </div>
          {diag && diag.attempts?.length === 0 && (
            <div className="text-[11px] text-white/45" data-testid="diagnostics-empty">No failed login attempts recorded.</div>
          )}
          {diag && diag.attempts?.length > 0 && (
            <div className="overflow-x-auto border border-white/10" data-testid="diagnostics-table">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-white/40 uppercase tracking-[0.2em] text-[9px] border-b border-white/10">
                    <th className="p-2 text-left">When</th>
                    <th className="p-2 text-left">Typed</th>
                    <th className="p-2 text-left">Normalized</th>
                    <th className="p-2 text-left">Outcome</th>
                    <th className="p-2 text-left">Error detail</th>
                  </tr>
                </thead>
                <tbody>
                  {diag.attempts.map((t, i) => (
                    <tr key={i} className="border-b border-white/5 align-top">
                      <td className="p-2 text-white/40 whitespace-nowrap">{t.at ? new Date(t.at).toLocaleString() : "—"}</td>
                      <td className="p-2 text-white font-mono">{t.identifier}</td>
                      <td className="p-2 text-white/50 font-mono">{t.normalized}</td>
                      <td className={`p-2 font-bold text-[9px] uppercase tracking-[0.2em] ${t.outcome === "server_error" ? "text-[#FF1B91]" : "text-yellow-300"}`}>
                        {t.outcome}{t.match_count > 1 ? ` (${t.match_count})` : ""}
                      </td>
                      <td className="p-2 text-white/50 max-w-[520px]">
                        {t.traceback ? <pre className="whitespace-pre-wrap text-[10px] text-[#FF1B91]/90">{t.traceback.slice(-700)}</pre> : (t.reason || "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Single-identifier probe — paste any email or phone and see exactly
            what the /log endpoint does with it. */}
        <div className="border-t border-white/10 pt-4">
          <div className="text-[9px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">Test one email or phone</div>
          <div className="flex flex-wrap gap-2">
            <input
              data-testid="login-probe-input"
              value={probeVal}
              onChange={(e) => setProbeVal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runProbe()}
              placeholder="6317745686 or someone@email.com"
              className="flex-1 min-w-[240px] bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-3 py-2 text-sm text-white"
            />
            <button
              type="button"
              data-testid="login-probe-btn"
              onClick={runProbe}
              disabled={probing}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] disabled:opacity-40 px-4 py-2"
            >
              <MagnifyingGlass size={12} weight="bold" /> {probing ? "Testing…" : "Test lookup"}
            </button>
          </div>
          {probe && (
            <pre data-testid="login-probe-result" className="mt-3 max-h-72 overflow-auto bg-black/60 border border-white/10 p-3 text-[10px] text-white/70 whitespace-pre-wrap">
              {JSON.stringify(probe, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}
